/**
 * buildCheck (DNKADM-0382 U3): the ONE fetch and the ONE comparison behind every
 * "is this tab running the build the server serves?" answer in the console.
 *
 * Two readers, one truth. `StaleBuildNotice` (DNKADM-0372) asks on a 10-minute timer and
 * on tab-visibility; the Help menu (DNKADM-0382 U2) asks when it opens. Both go through
 * {@link checkBuildCurrency}, which fetches `version.json` once (concurrent callers share
 * the in-flight promise), compares the served identifier with the compiled-in one, and
 * publishes the result here. They therefore can never disagree about what the server is
 * serving: they read the same snapshot. What they DO with it differs by design — the
 * snackbar honours its 30-minute dismiss hold, the menu answers whenever it is opened —
 * and that policy lives with each reader, not here.
 *
 * Failure is silent, exactly as `fetchServedBuildId` already is: a rejected fetch, a
 * non-2xx, or a malformed body resolves to `null`, publishes nothing, and leaves the last
 * good snapshot alone. An unidentifiable build (no compiled id) never fetches at all —
 * two blanks must never compare "equal".
 *
 * Reload is the operator's choice and lives here too ({@link reloadForNewBuild}) so the
 * snackbar's Reload and the menu's Reload are the same action, not two copies.
 */
import { useSyncExternalStore } from 'react';
import { fetchServedBuildId, getBuildId } from '@/config/buildId';

/** `current` — the server serves this tab's build; `stale` — it serves a different one. */
export type BuildCurrency = 'current' | 'stale';

export interface BuildCheckSnapshot {
  /** The latest successful comparison, or `null` before any check has succeeded. */
  result: BuildCurrency | null;
  /** `Date.now()` when {@link result} was established, or `null` before any check. */
  checkedAt: number | null;
  /** Bumps on every successful check, so a repeated `stale` still re-renders subscribers. */
  seq: number;
}

const INITIAL: BuildCheckSnapshot = { result: null, checkedAt: null, seq: 0 };

let snapshot: BuildCheckSnapshot = INITIAL;
let inFlight: Promise<BuildCurrency | null> | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): BuildCheckSnapshot {
  return snapshot;
}

/**
 * Compare the served build with the compiled-in one. Resolves `'current'`/`'stale'` on a
 * successful read and `null` on any failure or for an unidentifiable build. Concurrent
 * callers share one request. Never throws.
 */
export function checkBuildCurrency(): Promise<BuildCurrency | null> {
  const compiledId = getBuildId();
  if (compiledId === null) return Promise.resolve(null);
  if (inFlight !== null) return inFlight;
  const run = (async (): Promise<BuildCurrency | null> => {
    try {
      const servedId = await fetchServedBuildId();
      if (servedId === null) return null;
      const result: BuildCurrency = servedId === compiledId ? 'current' : 'stale';
      snapshot = { result, checkedAt: Date.now(), seq: snapshot.seq + 1 };
      emit();
      return result;
    } catch {
      // `fetchServedBuildId` already never throws; this keeps the contract if that changes.
      return null;
    } finally {
      inFlight = null;
    }
  })();
  inFlight = run;
  return run;
}

/** Subscribe a component to the shared snapshot (React external-store contract). */
export function useBuildCheckSnapshot(): BuildCheckSnapshot {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** The one reload path — the operator's explicit choice, never automatic. */
export function reloadForNewBuild(): void {
  window.location.reload();
}

/** Test-only: forget every published result and any in-flight request. */
export function __resetBuildCheckForTest(): void {
  snapshot = INITIAL;
  inFlight = null;
}
